import { useEffect, useState } from 'react';

interface FireworksProps {
  isActive: boolean;
}

interface FireworkBurst {
  id: number;
  x: number;
  y: number;
  delay: number;
  color: string;
}

const COLORS = ['#ff6b6b', '#4ecdc4', '#ffe66d', '#95e1d3', '#f38181', '#ffa07a', '#98d8c8', '#f7dc6f'];

// Pre-calculated positions for 16 particles in a circle
const PARTICLE_ANGLES = [
  { angle: 0, x: 0, y: -1 },      // Up
  { angle: 22.5, x: 0.382683, y: -0.92388 },
  { angle: 45, x: 0.707107, y: -0.707107 },
  { angle: 67.5, x: 0.92388, y: -0.382683 },
  { angle: 90, x: 1, y: 0 },      // Right
  { angle: 112.5, x: 0.92388, y: 0.382683 },
  { angle: 135, x: 0.707107, y: 0.707107 },
  { angle: 157.5, x: 0.382683, y: 0.92388 },
  { angle: 180, x: 0, y: 1 },     // Down
  { angle: 202.5, x: -0.382683, y: 0.92388 },
  { angle: 225, x: -0.707107, y: 0.707107 },
  { angle: 247.5, x: -0.92388, y: 0.382683 },
  { angle: 270, x: -1, y: 0 },    // Left
  { angle: 292.5, x: -0.92388, y: -0.382683 },
  { angle: 315, x: -0.707107, y: -0.707107 },
  { angle: 337.5, x: -0.382683, y: -0.92388 },
];

export function Fireworks({ isActive }: FireworksProps) {
  const [fireworks, setFireworks] = useState<FireworkBurst[]>([]);

  useEffect(() => {
    if (isActive) {
      // Create multiple firework bursts at strategic positions around the modal
      // Position them around the center area where the badge will be
      const positions = [
        { x: 20, y: 30 },   // Top left
        { x: 80, y: 25 },   // Top right
        { x: 15, y: 60 },   // Left center
        { x: 85, y: 55 },   // Right center
        { x: 50, y: 20 },   // Top center
        { x: 30, y: 70 },   // Bottom left
        { x: 70, y: 75 },   // Bottom right
        { x: 50, y: 80 },   // Bottom center
      ];

      const newFireworks = positions.map((pos, i) => ({
        id: i,
        x: pos.x,
        y: pos.y,
        delay: i * 200, // Stagger the animations
        color: COLORS[i % COLORS.length],
      }));
      setFireworks(newFireworks);

      // Clear fireworks after animation completes
      const timeout = setTimeout(() => {
        setFireworks([]);
      }, 4000);

      return () => clearTimeout(timeout);
    } else {
      setFireworks([]);
    }
  }, [isActive]);

  if (!isActive || fireworks.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden">
      {fireworks.map((firework) => (
        <div
          key={firework.id}
          className="firework-burst"
          style={{
            left: `${firework.x}%`,
            top: `${firework.y}%`,
            '--firework-color': firework.color,
            '--firework-delay': `${firework.delay}ms`,
          } as React.CSSProperties & { '--firework-color': string; '--firework-delay': string }}
        >
          {PARTICLE_ANGLES.map((particle, i) => (
            <div
              key={i}
              className="firework-particle"
              style={{
                '--particle-x': particle.x,
                '--particle-y': particle.y,
              } as React.CSSProperties & { '--particle-x': number; '--particle-y': number }}
            />
          ))}
        </div>
      ))}
      <style>{`
        .firework-burst {
          position: absolute;
          width: 4px;
          height: 4px;
          transform: translate(-50%, -50%);
        }

        .firework-particle {
          position: absolute;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--firework-color);
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          animation: firework-explode 1.2s ease-out forwards;
          animation-delay: var(--firework-delay);
          opacity: 0;
          box-shadow: 0 0 6px var(--firework-color);
        }

        @keyframes firework-explode {
          0% {
            opacity: 1;
            transform: translate(-50%, -50%) translateY(0) translateX(0) scale(1);
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate(-50%, -50%) 
              translateY(calc(var(--particle-y) * -100px)) 
              translateX(calc(var(--particle-x) * 100px)) 
              scale(0.3);
          }
        }
      `}</style>
    </div>
  );
}
